import { Grid } from '@mui/material';
import Badge from '@mui/material/Badge';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import dayjs, { Dayjs } from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import {
  getAutomaticCollectionNotificationsForMonth,
  getAvailableUsersForSearch,
  getManualCollectionNotificationsForMonth,
} from 'lib/api/collectionNotifications';
import {
  DayGroup,
  StakeholderGroup,
} from 'lib/api/collectionNotifications/types';
import GenericSearcher from 'lib/common/GlobalSearcher';
import EnhacedBox from 'lib/designSystem/BigBox';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  AutomaticNotification,
  AutomaticSummary,
} from './components/AutomaticSummary';
import ManualSummary from './components/ManualSummary';

dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  weekStart: 1,
});
const initialValue = dayjs();

function CalendarDay(
  props: PickersDayProps<Dayjs> & {
    highlightedAutomaticDays?: number[];
    highlightedManualDays?: number[];
    selectedDate?: Dayjs;
  },
) {
  const {
    highlightedAutomaticDays = [],
    highlightedManualDays = [],
    selectedDate,
    day,
    outsideCurrentMonth,
    ...other
  } = props;
  const dayAutomatic = highlightedAutomaticDays.includes(day.date());
  const dayManual = highlightedManualDays.includes(day.date());
  const highlight = dayAutomatic || dayManual;

  const isSelected = day.date() === selectedDate?.date();

  return (
    <Badge key={day.toString()} overlap="circular">
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        sx={{
          backgroundColor: highlight ? 'lightblue' : 'transparent',
          border: isSelected ? '1px solid black' : 'none',
        }}
      />
    </Badge>
  );
}

export default function NotificationsCalendar() {
  const [loading, setLoading] = useState(true);
  const [highlightedAutomaticDays, setHighlightedAutomaticDays] = useState<
    number[]
  >([]);
  const [highlightedManualDays, setHighlightedManualDays] = useState<number[]>(
    [],
  );
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [automaticNotifications, setAutomaticNotifications] = useState<
    AutomaticNotification[]
  >([]);
  const [filteredAutomaticNotifications, setFilteredAutomaticNotifications] =
    useState<AutomaticNotification[]>([]);
  const [manualNotifications, setManualNotifications] = useState<DayGroup[]>(
    [],
  );
  const [filteredManualNotifications, setFilteredManualNotifications] =
    useState<StakeholderGroup[]>([]);
  const dispatch = useDispatch();

  const [searchBusinessIdentifier, setSearchBusinessIdentifier] =
    useState<string>('');
  const [searchStakeholderIdentifier, setSearchStakeholderIdentifier] =
    useState<string>('');
  const [executiveId, setExecutiveId] = useState<string>('all');
  const [executiveOptions, setExecutiveOptions] = useState<any[]>([]);
  const [canHaveSubordinates, setCanHaveSubordinates] =
    useState<boolean>(false);

  const buildQuery = () => {
    const queryParams = new URLSearchParams();
    if (searchBusinessIdentifier) {
      queryParams.append('businessIdentifier', searchBusinessIdentifier);
    }
    if (searchStakeholderIdentifier) {
      queryParams.append('stakeholderIdentifier', searchStakeholderIdentifier);
    }
    if (executiveId && executiveId !== 'all') {
      queryParams.append('executiveId', executiveId);
    }
    return queryParams.toString();
  };

  const fetchHighlightedDays = async (date: Dayjs) => {
    setLoading(true);
    try {
      const query = buildQuery();
      const promises = [
        getAutomaticCollectionNotificationsForMonth(
          dispatch,
          date.format('MM-YY'),
          query,
        ),
        getManualCollectionNotificationsForMonth(
          dispatch,
          date.format('YYYY-MM-DD'),
          query,
        ),
      ];
      const [automaticData, manualData] = await Promise.all(promises);
      setHighlightedAutomaticDays(automaticData.map((item: any) => item.day));
      setHighlightedManualDays(
        (manualData as DayGroup[]).map((item) => item.day),
      );
      setAutomaticNotifications(automaticData);
      setManualNotifications(manualData as DayGroup[]);
      const day = date.date();
      const automaticNotificationsForDay = automaticData.filter(
        (item: any) => item.day === day,
      );
      const manualDayGroup = (manualData as DayGroup[]).find(
        (item) => item.day === day,
      );
      const manualNotificationsForDay = manualDayGroup
        ? manualDayGroup.stakeholders
        : [];
      setFilteredAutomaticNotifications(automaticNotificationsForDay);
      setFilteredManualNotifications(manualNotificationsForDay);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const triggerSearch = async () => {
    await fetchHighlightedDays(selectedDate);
  };

  useEffect(() => {
    void fetchHighlightedDays(initialValue);
    getAvailableUsersForSearch(dispatch)
      .then((res) => {
        setExecutiveOptions([
          {
            label: 'Todos',
            value: 'all',
          },
          ...res.users.map((item: any) => ({
            label: item.name,
            value: item.id,
          })),
        ]);
        setCanHaveSubordinates(res.canHaveSubordinates);
      })
      .catch((error) => {
        console.error('Error loading available users:', error);
      });
  }, []);

  const handleMonthChange = (date: Dayjs) => {
    setLoading(true);
    setHighlightedAutomaticDays([]);
    setHighlightedManualDays([]);
    setSelectedDate(date);
    void fetchHighlightedDays(date);
  };

  const handleDayChange = (date: Dayjs) => {
    const day = date.date();
    setSelectedDate(date);
    const automaticNotificationsForDay = automaticNotifications.filter(
      (item) => item.day === day,
    );
    const manualDayGroup = manualNotifications.find((item) => item.day === day);
    const manualNotificationsForDay = manualDayGroup
      ? manualDayGroup.stakeholders
      : [];
    setFilteredAutomaticNotifications(automaticNotificationsForDay);
    setFilteredManualNotifications(manualNotificationsForDay);
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      <HeaderWithActions
        title={
          'Acá encontrarás todas las gestiones y correos de cobranzas programados.'
        }
        variant={'body1'}
        buttons={[
          {
            name: 'Filtro',
            action: () => {},
            color: 'primary',
            customButton: (
              <GenericSearcher
                filters={[
                  {
                    type: 'text',
                    label: 'Rut Emisor',
                    value: searchBusinessIdentifier,
                    setter: setSearchBusinessIdentifier,
                  },
                  {
                    type: 'text',
                    label: 'Rut Deudor',
                    value: searchStakeholderIdentifier,
                    setter: setSearchStakeholderIdentifier,
                  },
                  ...(canHaveSubordinates
                    ? [
                        {
                          type: 'select' as const,
                          label: 'Ejecutivo',
                          value: executiveId,
                          setter: setExecutiveId,
                          options: executiveOptions,
                        },
                      ]
                    : []),
                ]}
                triggerSearch={triggerSearch}
              />
            ),
            hidden: loading,
          },
        ]}
      />
      <EnhacedBox>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            {loading ? (
              <CenteredCircularProgress relative />
            ) : (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  defaultValue={selectedDate}
                  loading={loading}
                  onMonthChange={handleMonthChange}
                  onChange={handleDayChange}
                  renderLoading={() => <DayCalendarSkeleton />}
                  slots={{
                    day: CalendarDay,
                  }}
                  slotProps={{
                    day: {
                      highlightedAutomaticDays,
                      highlightedManualDays,
                      selectedDate,
                    } as any,
                  }}
                />
              </LocalizationProvider>
            )}
          </Grid>
          <Grid item xs={8}>
            <AutomaticSummary
              selectedDate={selectedDate}
              filteredAutomaticNotifications={filteredAutomaticNotifications}
            />
            <ManualSummary
              selectedDate={selectedDate}
              filteredManualNotifications={filteredManualNotifications}
              loading={loading}
            />
          </Grid>
        </Grid>
      </EnhacedBox>
    </div>
  );
}
